'use client'

import React, { useState } from 'react'
import { isNil } from 'ramda'
import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from '@radix-ui/react-dialog'

import {
  bodyStyles,
  closeButtonStyles,
  footerStyles,
  headerStyles,
  headingStackStyles,
  modalStyles,
  overlayStyles,
  subtitleStyles,
  titleStyles,
} from './styles'
import { IconButton } from '@sidecar-components/molecules/IconButton'
import { IconSize } from '@sidecar-components/atoms/Icon'
import {
  Thumbnail,
  type ThumbnailProps,
} from '@sidecar-components/atoms/Thumbnail'
import { ModalProps } from './Modal.types'

const isThumbnailProps = (
  thumbnail: ModalProps['thumbnail'],
): thumbnail is ThumbnailProps => !React.isValidElement(thumbnail)

export const Modal = ({
  bodyClass = '',
  children,
  className = '',
  defaultOpen,
  footerContent,
  hideClose = false,
  mobileFullHeight = false,
  onOpenChange,
  open,
  overlay,
  size = 'sm',
  isSuppressed = false,
  testId,
  thumbnail,
  title,
  trigger,
  subtitle,
}: ModalProps) => {
  // we track "scrolled" so that we can add space/border when scrolled
  const [scrolled, setScrolled] = useState(false)
  const handleScroll = e => {
    setScrolled(e.target.scrollTop > 0)
  }

  // `visibility: hidden` rather than unmounting, so this modal keeps its scroll
  // position and local state while an inner modal is open (see `isSuppressed`).
  // `pointer-events-none` is belt-and-braces: a hidden element already ignores
  // clicks, but it also stops this modal's overlay swallowing them.
  const suppressedStyles = isSuppressed ? 'invisible pointer-events-none' : ''

  // the trigger that opens the modal
  const triggerContent = trigger && <Trigger asChild>{trigger}</Trigger>

  // Whether the header has anything in it. A modal that renders its own heading
  // in the body (see the exported header/title/subtitle styles) passes none of
  // these, and an empty header would still take up its own padding plus an empty
  // title line — a visible gap above the content.
  const hasHeaderContent =
    !!title || !!subtitle || thumbnail !== undefined || !hideClose

  // the modal content that goes within a variable Root element
  const portalContent = (
    <Portal>
      {/* clickable transparency */}
      {!isNil(overlay) ? (
        overlay
      ) : (
        <Overlay className={`${overlayStyles} ${suppressedStyles}`} />
      )}

      {/* dialog */}
      <Content
        className={`${modalStyles({ size, mobileFullHeight })} ${className} ${suppressedStyles}`}
        aria-modal="true"
        data-testid={testId}
        // this line removes an accessibility warning in tests
        aria-describedby={!subtitle && undefined}
      >
        {/* modal header with potential thumbnail, title, subtitle, and close button */}
        {hasHeaderContent && (
          <div className={headerStyles}>
            {/* optional thumbnail */}
            {thumbnail !== undefined &&
              (isThumbnailProps(thumbnail) ? (
                <Thumbnail
                  alt={thumbnail.alt}
                  icon={thumbnail.icon}
                  iconType={thumbnail.iconType}
                  photoSrc={thumbnail.photoSrc}
                  sentiment={thumbnail.sentiment}
                  thumbnailSize={IconSize.SM}
                  testId={thumbnail.testId}
                />
              ) : (
                thumbnail
              ))}

            {/* this keeps title and subtitle stacked vertically together */}
            <div className={headingStackStyles}>
              {/* Rendered even when empty, so Radix always finds the `Title`
                  its `aria-labelledby` points at. A modal that renders its own
                  title in the body passes no header content at all, which drops
                  this whole block — Radix keys every `Title` in a dialog to the
                  same id, so an empty one left here would out-rank the real one
                  on DOM order and blank the dialog's accessible name. */}
              <Title className={titleStyles}>{title}</Title>
              {!!subtitle && (
                <Description className={subtitleStyles}>{subtitle}</Description>
              )}
            </div>

            {/* Close button */}
            {!hideClose && (
              <Close asChild>
                <IconButton
                  className={closeButtonStyles}
                  label="Close"
                  icon="x"
                  hideTooltip={true}
                />
              </Close>
            )}
          </div>
        )}

        {/* modal body */}
        <div
          onScroll={handleScroll}
          className={`${bodyStyles({
            scrolled,
            hasFooter: !!footerContent,
            hasHeader: hasHeaderContent,
            mobileFullHeight,
          })} ${bodyClass}`}
        >
          {children}
        </div>

        {/* add modal footer only if there is at least 1 button */}
        {footerContent && <div className={footerStyles}>{footerContent}</div>}
      </Content>
    </Portal>
  )

  // If any of these values are set ("", undefined, etc), Radix turns the modal into a controlled component
  // where we have to manual control the state handling. That is needed sometimes. But for simple modals,
  // we want the modal to be uncontrolled and handle that complexity by itself. So the default is to not
  // pass any of the state control variables.
  return defaultOpen || open || onOpenChange ? (
    <Root defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={open}>
      {triggerContent}
      {portalContent}
    </Root>
  ) : (
    <Root>
      {triggerContent}
      {portalContent}
    </Root>
  )
}
